import React from 'react';
import { Play, Check, Eye } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useAuth } from '../contexts/AuthContext';
import TrackBuyButton from './TrackBuyButton';

const TrackCard = ({ track, tracks = [], compact = false, isPurchased = false }) => {
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  useAuth(); // keep cards reactive to auth changes
  const isCurrentTrack = currentTrack?.id === track.id;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPlayCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const handlePlay = (e) => {
    e.stopPropagation();
    playTrack(track, tracks);
  };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 p-3 bg-[#1A1A1A] border border-white/5 hover:border-[#D4AF37]/50 transition-all cursor-pointer group ${
          isCurrentTrack ? 'border-[#D4AF37]' : ''
        }`}
        onClick={handlePlay}
        data-testid={`track-card-${track.id}`}
      >
        <div className="w-10 h-10 relative flex-shrink-0">
          {track.cover_art_url ? (
            <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
            <Play size={16} className="text-[#D4AF37]" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{track.title}</p>
          <p className="text-xs text-[#A3A3A3] truncate">{track.genre}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#737373] tabular-nums">{formatDuration(track.duration)}</span>
          {!isPurchased && (
            <TrackBuyButton track={track} compact />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-[#1A1A1A] border border-white/5 hover:border-[#D4AF37]/50 transition-all card-hover overflow-hidden group ${
        isCurrentTrack ? 'border-[#D4AF37]' : ''
      }`}
      data-testid={`track-card-${track.id}`}
    >
      <div className="relative aspect-square">
        {track.cover_art_url ? (
          <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-3 transition-opacity">
          <button
            onClick={handlePlay}
            className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center text-black hover:scale-105 transition-transform"
            data-testid={`play-track-${track.id}`}
          >
            <Play size={20} className="ml-0.5" />
          </button>
        </div>
        {isCurrentTrack && isPlaying && (
          <div className="absolute bottom-2 right-2 flex gap-0.5">
            <div className="visualizer-bar" />
            <div className="visualizer-bar" />
            <div className="visualizer-bar" />
          </div>
        )}
        {isPurchased && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-green-600 text-xs uppercase tracking-wider flex items-center gap-1">
            <Check size={12} />
            Owned
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-medium truncate">{track.title}</h3>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">{track.genre}</span>
          {!isPurchased && (
            <span className="text-[#D4AF37] font-medium">${track.price?.toFixed(2) || '0.99'}</span>
          )}
        </div>
        {track.mood && (
          <span className="inline-block mt-2 px-2 py-1 bg-[#262626] text-xs text-[#A3A3A3]">{track.mood}</span>
        )}
        {track.play_count !== undefined && (
          <div className="flex items-center gap-1 mt-2 text-xs text-[#737373]">
            <Eye size={12} />
            <span>{formatPlayCount(track.play_count)} plays</span>
          </div>
        )}
        {!isPurchased && (
          <div className="mt-3" data-testid={`track-buy-row-${track.id}`}>
            <TrackBuyButton track={track} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackCard;
